import { useQuery } from '@tanstack/react-query'
import api from '@/consumer/lib/api'

export interface HoldingProduct {
  uuid: string
  name: string
  short_name: string
  template_name: string | null
  image_url: string | null
}

export interface HoldingWarrantyDocument {
  uuid: string
  label: string
  kind: 'BOOKLET' | 'TERMS' | 'FAQ' | 'OTHER'
  url: string | null
}

export interface HoldingWarranty {
  uuid: string
  class_name: string
  option_display_name: string
  active_from: string | null
  expires_at: string | null
  status: string
  is_lifetime: boolean
  term: string
  remaining_time_string: string
  duration_in_months?: number | null
  documents?: HoldingWarrantyDocument[]
}

export interface HoldingOrganisation {
  uuid: string
  name: string
  logo_url: string | null
}

export interface HoldingHostSummary {
  registration_number: string
  make: string
}

export interface HoldingAttribute {
  code_name: string
  display_name: string
  value: unknown
  field_type: string
}

export interface HoldingSummary {
  uuid: string
  product: HoldingProduct
  organisation: HoldingOrganisation
  warranty: HoldingWarranty | null
  host: HoldingHostSummary | null
  created_at: string | null
}

export interface HoldingHostDetail {
  kind: string
  name: string
  registration_number: string
  attribute_values: HoldingAttribute[]
}

export interface HoldingDetail extends Omit<HoldingSummary, 'host'> {
  attributes: HoldingAttribute[]
  host: HoldingHostDetail | null
  transaction_attributes: HoldingAttribute[]
  /**
   * Whether the tenant allows customers to view/download the warranty
   * certificate (RegistrationSettings.certificate_settings.enable_download).
   * The certificate entry point is hidden when false; the API also refuses
   * the fetch server-side.
   */
  certificate_download_enabled: boolean
}

export function useConsumerHoldings() {
  return useQuery<HoldingSummary[]>({
    queryKey: ['consumer-holdings'],
    queryFn: async () => {
      const res = await api.get('/holdings/')
      // Endpoint may return either a plain array or a DRF paginated
      // envelope `{ count, next, previous, results }` depending on
      // queryset size - normalise to an array.
      const data = res.data
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.results)) return data.results
      return []
    },
    staleTime: 30_000,
  })
}

export function useConsumerHolding(uuid: string | undefined) {
  return useQuery<HoldingDetail>({
    queryKey: ['consumer-holding', uuid],
    queryFn: async () => {
      const res = await api.get(`/holdings/${uuid}/`)
      return res.data
    },
    enabled: !!uuid,
    staleTime: 30_000,
  })
}
